/**
 * Helpers for calendar-day strings ("YYYY-MM-DD") as stored and sent by the API.
 *
 * Two traps these exist to avoid, both of which bite hardest in the Dominican
 * Republic (UTC-4), where every current customer runs:
 *
 * 1. `new Date("2026-08-16")` parses as UTC midnight. Formatting that in a zone
 *    behind UTC renders the *previous* day. We anchor at local noon instead, so
 *    the calendar day survives any offset between UTC-11 and UTC+12.
 * 2. `new Date().toISOString().slice(0, 10)` is UTC's today, not the user's. In
 *    the DR that flips to tomorrow at 8pm local. We build the string from local
 *    getters instead.
 */

const LOCALE = 'es-MX';

/** Format a Date as a local "YYYY-MM-DD" string (never via UTC toISOString). */
export function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Today as a local "YYYY-MM-DD" string. */
export function todayStr(): string {
  return toDateStr(new Date());
}

/**
 * Parse a "YYYY-MM-DD" string into a Date anchored at local noon, so the
 * calendar day is stable regardless of the viewer's timezone offset.
 * Returns null when the input is missing or unparseable.
 */
export function parseDateStr(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null;
  const d = new Date(`${String(dateStr).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Shift a "YYYY-MM-DD" string by whole days, returning a "YYYY-MM-DD" string. */
export function addDays(dateStr: string, days: number): string {
  const d = parseDateStr(dateStr);
  if (!d) return dateStr;
  d.setDate(d.getDate() + days);
  return toDateStr(d);
}

/** "2026-08-16" -> "16 ago 2026". Falls back to the raw input if unparseable. */
export function formatDateShort(dateStr: string | null | undefined): string {
  const d = parseDateStr(dateStr);
  if (!d) return dateStr ? String(dateStr) : '';
  return d.toLocaleDateString(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * "2026-08-16" -> "Hoy" / "Ayer" / "Mañana", else "sáb, 16 ago".
 * Used for date pickers and day headers where the relative label reads faster.
 */
export function formatDateLabel(dateStr: string | null | undefined): string {
  const d = parseDateStr(dateStr);
  if (!d) return dateStr ? String(dateStr) : '';
  const normalized = toDateStr(d);
  const today = todayStr();
  if (normalized === today) return 'Hoy';
  if (normalized === addDays(today, -1)) return 'Ayer';
  if (normalized === addDays(today, 1)) return 'Mañana';
  return d.toLocaleDateString(LOCALE, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}
